import { useState, useEffect } from 'react';
import { GripVertical, Link2, Clock, Phone, MessageCircle, Share2, Download, MousePointerClick, Star, Play, Video, Film, Image, Users, CalendarDays, Heart, AlertCircle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SECTIONS = [
  { id: 'links', label: 'Links', icon: Link2 },
  { id: 'social_icons', label: 'Social Icons', icon: Share2 },
  { id: 'video', label: 'Profile Video', icon: Video },
  { id: 'videos', label: 'Videos', icon: Film },
  { id: 'posters', label: 'Posters', icon: Image },
  { id: 'business_hours', label: 'Business Hours', icon: Clock },
  { id: 'contact_info', label: 'Contact Info', icon: Phone },
  { id: 'social_grid', label: 'Social Grid', icon: MessageCircle },
  { id: 'testimonials', label: 'Testimonials', icon: Star },
  { id: 'appreciations', label: 'Appreciations', icon: Heart },
  { id: 'network_stats', label: 'Network Stats', icon: Users },
  { id: 'review_generator', label: 'AI Review Generator', icon: Star },
  { id: 'exchange_contact', label: 'Exchange Contact', icon: Users },
  { id: 'book_appointment', label: 'Book Appointment', icon: CalendarDays },
  { id: 'cta_button', label: 'Call-to-Action', icon: MousePointerClick },
  { id: 'save_contact', label: 'Save Contact', icon: Download },
];

const getContentStatus = (profile, sectionId, links) => {
  switch (sectionId) {
    case 'links': return (links?.length > 0);
    case 'social_icons': return (profile?.social_links?.length > 0);
    case 'video': return !!profile?.video_header_url;
    case 'videos': return (profile?.reels?.length > 0);
    case 'posters': return (profile?.posters?.length > 0);
    case 'business_hours': {
      const bh = profile?.business_hours;
      return bh && typeof bh === 'object' && Object.values(bh).some(v => v);
    }
    case 'contact_info': return !!(profile?.phone || profile?.address || profile?.website);
    case 'social_grid': return true; // Assume available
    case 'testimonials': return (profile?.testimonials?.length > 0);
    case 'appreciations': return true; // Can't determine from profile alone
    case 'network_stats': return true; // Can't determine from profile alone
    case 'review_generator': return !!(profile?.google_review_url);
    case 'exchange_contact': return !!profile?.show_contact_form;
    case 'book_appointment': return !!profile?.booking_enabled;
    case 'cta_button': return !!(profile?.cta_button?.text || profile?.whatsapp_number);
    case 'save_contact': return true; // Always has content
    default: return true;
  }
};

const SectionOrderEditor = ({ profile, setProfile, links }) => {
  const [sections, setSections] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const savedOrder = profile?.section_order || DEFAULT_SECTIONS.map(s => s.id);
    const ordered = savedOrder
      .map(id => DEFAULT_SECTIONS.find(s => s.id === id))
      .filter(Boolean);
    DEFAULT_SECTIONS.forEach(s => {
      if (!ordered.find(o => o.id === s.id)) ordered.push(s);
    });
    setSections(ordered);
    setHiddenSections(profile?.hidden_sections || []);
  }, [profile?.section_order, profile?.hidden_sections]);

  const saveToServer = async (newOrder, newHidden) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile`, {
        section_order: newOrder,
        hidden_sections: newHidden,
      });
      setProfile(res.data);
      toast.success('Saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(sections);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setSections(items);
    await saveToServer(items.map(s => s.id), hiddenSections);
  };

  const toggleSection = async (sectionId) => {
    const newHidden = hiddenSections.includes(sectionId)
      ? hiddenSections.filter(id => id !== sectionId)
      : [...hiddenSections, sectionId];
    setHiddenSections(newHidden);
    await saveToServer(sections.map(s => s.id), newHidden);
  };

  return (
    <div className="max-w-2xl" data-testid="section-order-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Section Order</h1>
          <p className="text-[#A1A1AA] mt-1">Drag to reorder, toggle to show/hide sections on your public profile</p>
        </div>
        {saving && (
          <span className="text-xs text-violet-400 flex items-center gap-1.5 bg-violet-500/10 px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" /> Saving...
          </span>
        )}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
              {sections.map((section, index) => {
                const isHidden = hiddenSections.includes(section.id);
                const hasContent = getContentStatus(profile, section.id, links);
                return (
                <Draggable key={section.id} draggableId={section.id} index={index}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        snapshot.isDragging
                          ? 'bg-violet-500/10 border-violet-500/40 shadow-lg shadow-violet-500/5'
                          : isHidden
                            ? 'bg-[#0A0A0A] border-[#1E1E1E]'
                            : 'bg-[#121212] border-[#27272A] hover:border-[#3F3F46]'
                      } border`}
                      data-testid={`section-order-${section.id}`}>
                      <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing p-0.5">
                        <GripVertical className="w-4 h-4 text-[#525252]" />
                      </div>
                      <section.icon className={`w-4 h-4 flex-shrink-0 ${isHidden ? 'text-[#3F3F46]' : 'text-[#A1A1AA]'}`} />
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium ${isHidden ? 'text-[#52525B]' : 'text-white'}`}>{section.label}</span>
                        {!isHidden && !hasContent && (
                          <p className="text-[10px] text-amber-500/70 flex items-center gap-1 mt-0.5">
                            <AlertCircle className="w-3 h-3" /> No content added yet
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isHidden && <span className="text-[9px] text-[#52525B] bg-[#1E1E1E] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">Hidden</span>}
                        {!isHidden && hasContent && <span className="text-[9px] text-emerald-500/80 bg-emerald-500/10 px-1.5 py-0.5 rounded font-medium">Active</span>}
                        <Switch
                          data-testid={`toggle-${section.id}`}
                          checked={!isHidden}
                          onCheckedChange={() => toggleSection(section.id)}
                          className="scale-75"
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default SectionOrderEditor;
